import type { Product } from '../insurance-products/index.ts'

/**
 * What happens to the existing contracts of a retired product. New business is closed for every retired product, and
 * their existing contracts are neither endorsed nor renewed.
 */
type ExistingContractsHandling = 'terminate-at-expiry' | 'none'

type RetiredProduct = {
  existingContracts: ExistingContractsHandling
  /** The product that takes its place for new business, which links and tools built for it now select. */
  successor: Product | undefined
}

const RETIRED_PRODUCTS: Partial<Record<Product, RetiredProduct>> = {
  // Wakam stopped carrying these products on 1 October 2026.
  mrpw: { existingContracts: 'terminate-at-expiry', successor: 'fr-mrpaxe' },
  'fr-rcpw': { existingContracts: 'terminate-at-expiry', successor: 'fr-rcpaxe' },
  'es-mrpw': { existingContracts: 'terminate-at-expiry', successor: undefined },
  // Never sold in production, so there is no contract to terminate.
  'fr-rccw': { existingContracts: 'none', successor: undefined },
}

export function isRetiredProduct(product: Product): boolean {
  return RETIRED_PRODUCTS[product] !== undefined
}

/** The product that replaces a retired product, or undefined when the product is not retired or has no successor. */
export function getRetiredProductSuccessor(product: Product): Product | undefined {
  return RETIRED_PRODUCTS[product]?.successor
}

export function includesRetiredProduct(selectedProducts: readonly Product[]): boolean {
  return selectedProducts.some(isRetiredProduct)
}

const IS_TERMINATED_AT_EXPIRY: Record<ExistingContractsHandling, boolean> = {
  'terminate-at-expiry': true,
  none: false,
}

export function isRetiredProductTerminatedAtExpiry(product: Product): boolean {
  const retiredProduct = RETIRED_PRODUCTS[product]
  return retiredProduct !== undefined && IS_TERMINATED_AT_EXPIRY[retiredProduct.existingContracts]
}
